/**
 * createStaleResponseGuard — the ONE "latest request wins" guard behind every frontend
 * read whose answer can arrive after the thing it was asked for has moved on.
 *
 * A guard is a pair of facts, and the apply site rests on nothing else:
 *
 *   1. The SEQUENCE. `claim()` hands the request a number; `isCurrent(token)` is false once
 *      any later `claim()` or `invalidate()` has moved the latest number on. This is the
 *      request-ordering fact: among reads for the same subject, only the newest may land
 *      (an A → B → A hover or selection must drop the FIRST read of A even though A is the
 *      subject standing now — between the two reads the viewer may have unlocked a
 *      fragment, and the older answer would visibly overwrite the newer one).
 *
 *   2. The IDENTITY, when the guard is built over a reactive source. `claim()` reads
 *      `current()` at claim time and `isCurrent(token)` reads it again at apply time; the
 *      two must agree. This is the subject-currency fact: a read for the record that WAS
 *      focal must not label the record that IS focal, even when nothing newer was ever
 *      requested (a cleared source, a type with no resolver).
 *
 * WHY TWO FACTS AND NOT ONE. A composable driven by an imperative call — `lookup(lat, lng)`,
 * `fetch(descriptor)`, `reset()` — claims its number INSIDE that call, so the claim is
 * synchronous with the source write by construction and the sequence alone is the whole
 * guard: pass no `current`. A composable driven by a reactive source claims from a
 * `watch` callback, and a pre-flush watcher is a MICROTASK that runs after any awaited
 * fetch continuation queued before it. A response that resolves in the same synchronous
 * turn as the source write therefore resumes BEFORE the watcher has claimed the next
 * number, sees a sequence that still matches, and writes onto the wrong subject. No
 * ordering of jobs is promised to a watcher, so the guard does not lean on one: the
 * source ref itself is written synchronously, which makes `current()` at apply time an
 * ordering-independent fact. That is the arm that closes the window, and it is why a
 * reactive adopter claims per REQUEST (a source change that issues nothing needs no
 * claim — the identity test already refuses the in-flight read) while an imperative
 * adopter also `invalidate()`s on every reset and teardown (nothing else advances its
 * number).
 *
 * `current()` returns a primitive (a `"<type>:<id>"` string, null for "no subject");
 * identities are compared with `Object.is`, never structurally.
 *
 * Adopters: `useFocalDisplayName` (reactive source), `useGlanceSummary`,
 * `useEventContext`, `useMediaManifest`, `useReverseGeocode` (imperative).
 *
 * @param {(() => any) | null} [current] - the identity of the subject standing now, for a
 *   guard over a reactive source; omit for a guard whose claims are the source writes.
 * @returns {{ claim: () => {seq: number, identity: any}, invalidate: () => void,
 *   isCurrent: (token: {seq: number, identity: any} | null | undefined) => boolean }}
 */
export function createStaleResponseGuard(current = null) {
  let latest = 0;

  /** Claim the next number (and, over a reactive source, the identity standing now). */
  function claim() {
    latest += 1;
    return Object.freeze({ seq: latest, identity: current === null ? undefined : current() });
  }

  /** Advance the number without a request: a reset, a teardown, a new hover. */
  function invalidate() {
    latest += 1;
  }

  /** True only while no later claim or invalidation exists AND the subject still stands. */
  function isCurrent(token) {
    if (!token || token.seq !== latest) return false;
    if (current === null) return true;
    return Object.is(current(), token.identity);
  }

  return { claim, invalidate, isCurrent };
}

export default createStaleResponseGuard;
